import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export default function Privacy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="rh-legal-page">
      {/* Top bar (matches /services topbar style) */}
      <header className="rh-legal-topbar">
        <Link to="/" className="rh-legal-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </Link>
        <Link to="/" className="rh-legal-brand">
          <img src={require('./assets/logo2.png')} alt="Reliable Handyman" className="rh-legal-brand-img" />
        </Link>
        <div className="rh-legal-spacer" aria-hidden="true" />
      </header>

      <main className="rh-legal-content">
        <header className="rh-legal-header">
          <div className="rh-legal-eyebrow">Legal</div>
          <h1>Privacy Policy</h1>
          <p className="rh-legal-effective">Effective: June 4, 2026 · Last updated: June 10, 2026</p>
        </header>

        <section className="rh-legal-section">
          <p className="rh-legal-lede">
            Reliable Handyman LLC ("Reliable Handyman," "we," "us," or "our") respects
            your privacy. This policy explains what information we collect when you
            visit our website or use our services, how we use it, and your choices
            about that information.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>1. Who we are</h2>
          <p>
            Reliable Handyman LLC is a home services company operating on Long
            Island, primarily in Nassau County, New York, with Suffolk County
            visits available on request. You can reach us at:
          </p>
          <ul>
            <li>Email: <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a></li>
            <li>Phone: <a href="tel:+15163869923">(516) 386-9923</a></li>
            <li>Mailing address: 2417 Jericho Tpke, Garden City Park, NY 11040</li>
          </ul>
        </section>

        <section className="rh-legal-section">
          <h2>2. Information we collect</h2>
          <p>We collect the following information when you interact with our site:</p>
          <ul>
            <li>
              <strong>Contact details</strong> you provide on our forms: name, phone
              number, email address, and service address.
            </li>
            <li>
              <strong>Project information</strong> you share: descriptions of work
              you need done, photos of the project area, preferred service times,
              and selected flat-rate services.
            </li>
            <li>
              <strong>Automated usage data</strong> such as your IP address,
              browser type, device type, pages visited, and referring URLs. This
              is collected through standard web analytics tools (see Section 5).
            </li>
            <li>
              <strong>Location data</strong> derived from your service address
              (zip code, neighborhood) to determine whether we serve your area.
              We do not access GPS or device-level location.
            </li>
          </ul>
        </section>

        <section className="rh-legal-section">
          <h2>3. How we use your information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Respond to your service requests and estimates</li>
            <li>Schedule, confirm, and follow up on appointments</li>
            <li>Send transactional emails and text messages related to your service (appointment confirmations, invoices, follow-up)</li>
            <li>Process payments after work is completed</li>
            <li>Improve our website and services based on usage patterns</li>
            <li>Comply with legal obligations (tax records, insurance documentation)</li>
            <li>With your consent, send occasional marketing emails or text messages about services, seasonal offers, or referral programs</li>
          </ul>
          <p>
            We do not sell your personal information to third parties, ever.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>4. SMS and text messaging</h2>
          <p>
            By submitting a service request or estimate form, you consent to receive
            text messages from Reliable Handyman LLC related to your request,
            including appointment scheduling, confirmations, follow-up questions,
            and quote responses. Message frequency varies based on your service
            needs. Message and data rates may apply from your carrier.
          </p>
          <p>
            You can opt out of non-essential text messages at any time by replying
            STOP to any of our messages, or by emailing us at{' '}
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>.
            We will still send transactional messages necessary to complete a
            scheduled job (such as a tech-arriving notification on the day of service).
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>5. Cookies and analytics</h2>
          <p>
            Our website uses cookies and similar technologies to function properly
            and to understand how visitors use our site. We use the following
            third-party services:
          </p>
          <ul>
            <li><strong>Google Maps</strong> for address autocomplete on our forms</li>
            <li><strong>Firebase</strong> (a Google service) to host our website and store your service requests</li>
            <li><strong>SendGrid</strong> to send email notifications to our team and to you</li>
            <li><strong>Meta Pixel</strong> to measure the effectiveness of our advertising</li>
            <li><strong>Google Analytics</strong> (if enabled) to understand site traffic patterns</li>
          </ul>
          <p>
            Each of these services has its own privacy policy governing how they
            handle data they receive. You can disable cookies in your browser
            settings, but parts of our site may not function correctly without them.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>6. How we share your information</h2>
          <p>We share your information only in these limited circumstances:</p>
          <ul>
            <li>
              <strong>With our service providers</strong> who help us operate the
              business — email delivery, payment processing, scheduling software,
              cloud hosting. These providers are bound by contracts to use your
              information only for the purposes we direct.
            </li>
            <li>
              <strong>With our technicians</strong> on the job assigned to your
              service. They receive your name, address, phone, project details,
              and any photos you submitted.
            </li>
            <li>
              <strong>When required by law</strong> — for example, in response to
              a subpoena, court order, or to comply with tax or regulatory
              obligations.
            </li>
            <li>
              <strong>To protect our rights</strong> — to prevent fraud, enforce
              our terms, or protect the safety of our customers, employees, or others.
            </li>
            <li>
              <strong>In a business transfer</strong> — if Reliable Handyman LLC
              is acquired, merged, or sells substantially all its assets, your
              information may be transferred as part of that transaction.
            </li>
          </ul>
        </section>

        <section className="rh-legal-section">
          <h2>7. Data retention</h2>
          <p>
            We keep your information for as long as your account is active or as
            needed to provide you services and comply with our legal obligations.
            Service records (including photos and invoices) are retained for at
            least 7 years for tax and warranty purposes.
          </p>
          <p>
            If you would like us to delete your information, contact us at{' '}
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>.
            Note that we may need to retain certain records to comply with legal
            obligations.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>8. Your rights</h2>
          <p>
            Depending on where you live, you may have additional rights regarding
            your personal information:
          </p>
          <ul>
            <li><strong>Right to access</strong> — request a copy of the personal information we have about you</li>
            <li><strong>Right to correct</strong> — ask us to fix inaccurate information</li>
            <li><strong>Right to delete</strong> — ask us to delete your personal information</li>
            <li><strong>Right to opt out</strong> — opt out of marketing communications at any time</li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{' '}
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>.
            We will respond within 30 days.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>9. Children's privacy</h2>
          <p>
            Our services are not directed to children under 18, and we do not
            knowingly collect personal information from anyone under 18. If you
            believe we have collected information from a child, please contact us
            and we will delete it.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>10. Data security</h2>
          <p>
            We use reasonable security measures to protect your information,
            including encrypted data transmission (HTTPS) and access controls on
            our internal systems. However, no security measure is perfect, and
            we cannot guarantee that your data will never be accessed without
            authorization. If we become aware of a security breach affecting your
            personal information, we will notify you in accordance with applicable
            law.
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>11. Changes to this policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the
            updated version on this page with a new "Last updated" date. For
            material changes, we will make reasonable efforts to notify you (for
            example, via email or a notice on our website).
          </p>
        </section>

        <section className="rh-legal-section">
          <h2>12. Contact us</h2>
          <p>
            If you have questions or concerns about this Privacy Policy or how
            we handle your information, contact us at:
          </p>
          <ul>
            <li><a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a></li>
            <li><a href="tel:+15163869923">(516) 386-9923</a></li>
            <li>Reliable Handyman LLC<br />2417 Jericho Tpke<br />Garden City Park, NY 11040</li>
          </ul>
        </section>

        <div className="rh-legal-footer-nav">
          <Link to="/terms">View Terms of Service →</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="svc-marketing-footer">
        <div className="svc-container svc-marketing-footer-inner">
          <Link to="/" className="svc-marketing-footer-brand" style={{ textDecoration: 'none' }}>
            <img src={require('./assets/logo2.png')} alt="Reliable Handyman" style={{ height: 26 }} />
          </Link>
          <div className="svc-marketing-footer-contact">
            <a href="tel:+15163869923">(516) 386-9923</a>
            <a href="mailto:help@getreliablehandyman.com">help@getreliablehandyman.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
